import type { IntlShape } from 'react-intl';
import { byteLimitLabel, secondsLimitLabel } from 'fym-shared/src/music24/postVideoRules';
import {
  canStartPublish,
  isOnChannel,
  publishBadgeState,
  type SlotChannelGroup,
  type AssetPublishRefusal,
  type OneOffChannel,
  type SerializedAssetPublish,
} from 'fym-shared/src/music24/oneOffPublish';
import { publishMessages } from './publishMessages';

/** The brand name of a one-off channel. Brand names are not translated. */
export const ONE_OFF_CHANNEL_NAMES: Readonly<Record<OneOffChannel, string>> = {
  youtube: 'YouTube',
  spotifyCanvas: 'Spotify',
  facebook: 'Facebook',
  instagram: 'Instagram',
  appleMusic: 'Apple Music',
  youtubeBanner: 'YouTube',
  xBanner: 'X',
  facebookBanner: 'Facebook',
  spotifyHeader: 'Spotify',
  youtubeThumbnail: 'YouTube',
  // A song channel (spec 2026-10-15): the kit badges never show it; "Where it plays" uploads it.
  soundcloud: 'SoundCloud',
};

/**
 * `waits` (amber) is a manual step that waits for the user (design assets-rethink b1 legend):
 * the Spotify Canvas and the Apple Music motion artwork until the user marks them. The profile
 * banners and the thumbnail stay `off` (dashed) until marked.
 */
export type PublishBadgeTone = 'on' | 'off' | 'pending' | 'problem' | 'waits';

/** The manual marks whose badge is amber until marked (decision 6, 2026-10-09). */
const WAITING_MARK_CHANNELS: ReadonlyArray<OneOffChannel> = ['spotifyCanvas', 'appleMusic'];

/** The "I set it" marks of the Kit tab: a profile banner of one site, the Spotify header, the YouTube thumbnail. */
export const SET_MARK_CHANNELS: ReadonlyArray<OneOffChannel> = [
  'youtubeBanner',
  'xBanner',
  'facebookBanner',
  'spotifyHeader',
  'youtubeThumbnail',
];

/** True when the badge of this record is the amber "waits for you" state (the Kit summary counts it). */
export function isWaitingMark(channel: OneOffChannel, record: SerializedAssetPublish | null): boolean {
  return WAITING_MARK_CHANNELS.includes(channel) && record?.status !== 'published';
}

export interface PublishBadgeText {
  text: string;
  tone: PublishBadgeTone;
}

/** "Oct 21, 09:00" in the language of the app. */
export function publishTimeText(intl: IntlShape, iso: string): string {
  return intl.formatDate(new Date(iso), { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

/**
 * The text and the tone of one channel badge on an asset card: "On YouTube", "Not on YouTube",
 * "On YouTube at Oct 21, 09:00", "Uploading to YouTube", "YouTube upload failed", "Removed from
 * YouTube", "Private on YouTube". A Canvas says "Canvas set in Spotify" or "Canvas not set in
 * Spotify". Pure (the intl is the only input that is not data).
 */
export function publishBadgeText(
  intl: IntlShape,
  channel: OneOffChannel,
  record: SerializedAssetPublish | null
): PublishBadgeText {
  const name = ONE_OFF_CHANNEL_NAMES[channel];
  const state = publishBadgeState(record);
  if (channel === 'spotifyCanvas') {
    return state.kind === 'published'
      ? { text: intl.formatMessage(publishMessages.canvasSet), tone: 'on' }
      : { text: intl.formatMessage(publishMessages.canvasNotSet), tone: 'waits' };
  }
  // design: assets-rethink b1 — the Apple Music motion artwork waits in amber until marked.
  if (channel === 'appleMusic') {
    return state.kind === 'published'
      ? { text: intl.formatMessage(publishMessages.badgePublished, { channel: name }), tone: 'on' }
      : { text: intl.formatMessage(publishMessages.badgeNotYet, { channel: name }), tone: 'waits' };
  }
  // design: assets-rethink b1 — a banner or the thumbnail: "Set on X" or "Not set on X" (dashed).
  if (SET_MARK_CHANNELS.includes(channel)) {
    return state.kind === 'published'
      ? { text: intl.formatMessage(publishMessages.badgeSet, { channel: name }), tone: 'on' }
      : { text: intl.formatMessage(publishMessages.badgeNotSet, { channel: name }), tone: 'off' };
  }
  switch (state.kind) {
    case 'none':
      return { text: intl.formatMessage(publishMessages.badgeNone, { channel: name }), tone: 'off' };
    case 'published':
      return { text: intl.formatMessage(publishMessages.badgePublished, { channel: name }), tone: 'on' };
    case 'scheduled':
      return state.publishAt === null
        ? { text: intl.formatMessage(publishMessages.badgeUploading, { channel: name }), tone: 'pending' }
        : {
            text: intl.formatMessage(publishMessages.badgeScheduled, {
              channel: name,
              time: publishTimeText(intl, state.publishAt),
            }),
            tone: 'pending',
          };
    case 'waiting':
      return { text: intl.formatMessage(publishMessages.badgeWaiting, { channel: name }), tone: 'pending' };
    case 'uploading':
      return { text: intl.formatMessage(publishMessages.badgeUploading, { channel: name }), tone: 'pending' };
    case 'failed':
      return { text: intl.formatMessage(publishMessages.badgeFailed, { channel: name }), tone: 'problem' };
    case 'removed':
      if (state.reason === 'replaced') {
        return { text: intl.formatMessage(publishMessages.badgeReplaced, { channel: name }), tone: 'off' };
      }
      return state.reason === 'private'
        ? { text: intl.formatMessage(publishMessages.badgePrivate, { channel: name }), tone: 'problem' }
        : { text: intl.formatMessage(publishMessages.badgeRemoved, { channel: name }), tone: 'problem' };
    case 'canceled':
      return { text: intl.formatMessage(publishMessages.badgeCanceled, { channel: name }), tone: 'off' };
  }
}

/**
 * The ONE badge of a channel on a Kit slot (owner fix 2026-10-10). A slot with one file shows the
 * badge of that file. A slot with several files (the four YouTube thumbnails, the two motion
 * artwork files) summarises them: none marked = the badge of no record ("Not set on YouTube"), all
 * marked = the badge of a marked record ("Set on YouTube"), some = "Set on YouTube · 2 of 4".
 */
export function slotBadgeText(
  intl: IntlShape,
  group: SlotChannelGroup<{ channel: OneOffChannel; record: SerializedAssetPublish | null }>
): PublishBadgeText {
  const { channel, entries, total, marked } = group;
  if (total <= 1) {
    return publishBadgeText(intl, channel, entries[0]?.record ?? null);
  }
  if (marked === 0) {
    // No file is on the channel: the record of a file that is on its way (an upload) says more.
    const busy = entries.find((entry) => entry.record !== null && !canStartPublish(entry.record));
    return publishBadgeText(intl, channel, busy?.record ?? null);
  }
  if (marked === total) {
    return publishBadgeText(intl, channel, entries.find((entry) => isOnChannel(entry.record))?.record ?? null);
  }
  const values = { channel: ONE_OFF_CHANNEL_NAMES[channel], count: marked, total };
  return SET_MARK_CHANNELS.includes(channel) || channel === 'spotifyCanvas'
    ? { text: intl.formatMessage(publishMessages.badgeSetSome, values), tone: 'pending' }
    : {
        text: intl.formatMessage(publishMessages.badgeOnSome, values),
        tone: WAITING_MARK_CHANNELS.includes(channel) ? 'waits' : 'pending',
      };
}

/** True when the slot badge of this channel is amber: a waiting mark with a file not marked yet. */
export function isWaitingSlotMark(group: { channel: OneOffChannel; total: number; marked: number }): boolean {
  return WAITING_MARK_CHANNELS.includes(group.channel) && group.marked < group.total;
}

/** "4:05" for a length in seconds. */
function lengthText(seconds: number): string {
  const whole = Math.round(seconds);
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
}

/** Why Instagram refuses the file as a Reel (the `refusal` of the defaults answer). */
export function publishRefusalText(intl: IntlShape, refusal: AssetPublishRefusal): string {
  switch (refusal.reason) {
    case 'size':
      return intl.formatMessage(publishMessages.refusalSize, {
        size: byteLimitLabel(refusal.sizeBytes),
        max: byteLimitLabel(refusal.maxBytes),
      });
    case 'tooLong':
      return intl.formatMessage(publishMessages.refusalTooLong, {
        length: lengthText(refusal.durationSeconds),
        max: secondsLimitLabel(refusal.maxSeconds),
      });
    case 'tooShort':
      return intl.formatMessage(publishMessages.refusalTooShort, {
        length: lengthText(refusal.durationSeconds),
        min: secondsLimitLabel(refusal.minSeconds),
      });
    case 'type':
    case 'aspect':
      return intl.formatMessage(publishMessages.refusalOther);
  }
}

/** The sentence of a failed record (`failReason` of the API). */
export function publishFailText(intl: IntlShape, reason: string | null): string {
  switch (reason) {
    case 'size':
    case 'tooLong':
    case 'tooShort':
    case 'aspect':
    case 'type':
      return `${intl.formatMessage(publishMessages.refusalOther)} ${intl.formatMessage(publishMessages.refusalFallback)}`;
    case 'renderFailed':
      return intl.formatMessage(publishMessages.failRenderFailed);
    case 'reconnect':
      return intl.formatMessage(publishMessages.failReconnect);
    case 'fileGone':
      return intl.formatMessage(publishMessages.failFileGone);
    case 'platformRefused':
      return intl.formatMessage(publishMessages.failPlatformRefused);
    case null:
    default:
      return intl.formatMessage(publishMessages.failUploadFailed);
  }
}

/** The sentence of a refused request (`cause.code` of the API). */
export function publishErrorText(intl: IntlShape, channel: OneOffChannel, error: unknown): string {
  const cause = (error as { m24Limit?: { code?: unknown } } | null)?.m24Limit;
  const name = ONE_OFF_CHANNEL_NAMES[channel];
  switch (cause?.code) {
    case 'M24_PUBLISH_TIME_INVALID':
      return intl.formatMessage(publishMessages.errorTimeInvalid);
    case 'M24_PUBLISH_ALREADY':
      return intl.formatMessage(publishMessages.errorAlready, { channel: name });
    case 'M24_PUBLISH_NOT_CONNECTED':
      return intl.formatMessage(publishMessages.errorNotConnected, { channel: name });
    case 'M24_PUBLISH_NEEDS_RECONNECT':
      return intl.formatMessage(publishMessages.actionReconnect, { channel: name });
    case 'M24_PUBLISH_PLATFORM_REFUSED':
      return intl.formatMessage(publishMessages.failPlatformRefused);
    case 'M24_PUBLISH_VIDEO_REFUSED':
      return intl.formatMessage(publishMessages.refusalOther);
    case 'M24_PUBLISH_REPLACE_CHOICE_REQUIRED':
      return intl.formatMessage(publishMessages.replaceTitle);
    default:
      return intl.formatMessage(publishMessages.errorGeneric);
  }
}
